import { motion } from 'framer-motion';
import { Play, Lock, Check, Clock, Sparkles, ArrowRight } from 'lucide-react';
import {
  Eyebrow, RaceBib, Tom, Crowd, MarginNote, MarkerHighlight,
} from '@/components/foundry';

interface Lesson {
  num: string;
  title: string;
  duration: string;
  state: 'done' | 'current' | 'next' | 'locked';
  preview: string;
}

const LESSONS: Lesson[] = [
  {
    num: '01',
    title: 'The Pocket Genius problem',
    duration: '6 min',
    state: 'done',
    preview: 'Why your members already have a smarter library than yours, and what that means for everything you teach.',
  },
  {
    num: '02',
    title: 'The one shift that flips AI from threat to advantage',
    duration: '8 min',
    state: 'current',
    preview: 'Your method, your standards, your sequence — steering the model. Not theory; a concrete framework.',
  },
  {
    num: '03',
    title: 'The no-sales-call funnel',
    duration: '11 min',
    state: 'next',
    preview: 'How PJPT replaced 11h/wk of sales calls with a personalised AI bridge that converts cold traffic.',
  },
  {
    num: '04',
    title: 'Your Steering Layer',
    duration: '7 min',
    state: 'locked',
    preview: 'The part of your business AI can\'t reproduce — and why making it explicit is the fastest growth move.',
  },
  {
    num: '05',
    title: 'What changes in 30 days',
    duration: '9 min',
    state: 'locked',
    preview: 'The first working version of your AI-native system, what it looks like, and the path to ship it.',
  },
];

interface Props {
  onOpenLesson?: (num: string) => void;
  onOpenBreakthrough?: () => void;
}

export default function KickstartHome({ onOpenLesson, onOpenBreakthrough }: Props = {}) {
  const completed = LESSONS.filter((l) => l.state === 'done').length;
  const currentLesson = LESSONS.find((l) => l.state === 'current') ?? LESSONS[0];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-6 lg:px-8">
      {/* HERO band */}
      <section className="relative mb-10 grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
        <div>
          <div className="mb-4 flex items-center gap-2">
            <RaceBib n="04" label="Cohort live · 21 builders" tone="race" size="md" shadow="md" />
            <RaceBib n="" label="No sales calls" tone="ink" size="sm" shadow="sm" />
          </div>
          <h1 className="font-geist text-[36px] font-semibold leading-[1.02] tracking-[-0.035em] text-fd-text [text-wrap:balance] sm:text-[44px] lg:text-[52px] lg:leading-[0.98] lg:tracking-[-0.04em]">
            5 lessons. One{' '}
            <span
              className="inline-block rounded-[6px] bg-fd-text px-2.5 pb-0.5 text-fd-accent sm:px-3"
              style={{ boxShadow: '4px 4px 0 0 var(--fd-accent)', lineHeight: '1.05' }}
            >
              no-sales-call
            </span>{' '}
            system. Ready in <MarkerHighlight tone="race"><span>under an hour</span></MarkerHighlight>.
          </h1>
          <p className="mt-5 max-w-[540px] text-[15px] leading-[1.55] text-fd-text-muted sm:text-[16px] lg:text-[17px]">
            Welcome to Kickstart. Five short lessons walk you through the AI-native shift, the
            method, and the funnel — then unlock <b>The Breakthrough</b> with your first concrete
            move. Pause anywhere; resume right where Tom left off.
          </p>

          {/* Continue CTA */}
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              onClick={() => onOpenLesson?.(currentLesson.num)}
              className="inline-flex h-12 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[14px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px"
              style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              Continue · Lesson {currentLesson.num}
            </button>
            <button className="inline-flex h-12 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-bg px-5 font-geist text-[14px] font-semibold text-fd-text transition-colors hover:bg-fd-text hover:text-fd-bg">
              See what's inside
            </button>
          </div>

          {/* Progress strip */}
          <div className="mt-6 flex items-center gap-3 max-w-[540px]">
            <Eyebrow><b>{completed}</b>/5 LESSONS</Eyebrow>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-fd-surface-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(completed / 5) * 100}%` }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="h-full bg-fd-accent"
              />
            </div>
            <Eyebrow><b>{5 - completed}</b> TO GO</Eyebrow>
          </div>
        </div>

        {/* Hero asset — Tom pointing into the lesson grid below */}
        <div className="relative hidden lg:flex lg:items-end lg:justify-center">
          <Tom variant="pointing-right" className="h-[280px] w-auto" />
          <MarginNote tilt={-4} tone="teal" size="md" className="absolute right-2 top-4">
            5 short videos.<br />
            <span className="text-fd-text-muted">first one's the magnet.</span>
          </MarginNote>
        </div>
      </section>

      {/* SET THE BAR rules card */}
      <section
        className="relative mb-10 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-6 sm:p-7"
        style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Eyebrow className="mb-2">SET THE BAR · BEFORE YOU START</Eyebrow>
            <h2 className="font-geist text-[22px] font-medium tracking-[-0.015em]">
              Three rules that make the next hour worth it.
            </h2>
          </div>
          <RaceBib n="" label="Cutoff · Friday 9pm GMT" tone="race" size="md" shadow="sm" />
        </div>

        <ol className="mt-5 grid gap-3 sm:grid-cols-3">
          {[
            { rule: 'Watch in order', why: 'Each lesson sets up the next. Skipping kills the build.' },
            { rule: 'Do The Lab Work', why: 'A 2-min reflection per lesson. The Breakthrough only unlocks if you ship.' },
            { rule: 'Finish before the cutoff', why: 'Cohort 04 closes Friday 9pm GMT. After that the warm bridge resets.' },
          ].map((r, i) => (
            <li
              key={r.rule}
              className="rounded-[10px] border border-fd-border bg-fd-bg p-4"
            >
              <div className="mb-1.5 flex items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-[4px] bg-fd-accent font-geist-mono text-[12px] font-bold text-fd-text">
                  {i + 1}
                </span>
                <h3 className="font-geist text-[14px] font-semibold tracking-[-0.005em]">{r.rule}</h3>
              </div>
              <p className="text-[12.5px] leading-[1.5] text-fd-text-muted">{r.why}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* LESSON GRID — Erik Hoffmann large video cards */}
      <section className="mb-10">
        <div className="mb-4 flex items-baseline justify-between">
          <Eyebrow>5 LESSONS · ABOUT 41 MIN TOTAL</Eyebrow>
          <span className="hidden font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle sm:block">
            Pause anywhere · resume from any device
          </span>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {LESSONS.map((l) => (
            <LessonCard
              key={l.num}
              lesson={l}
              onOpen={() => onOpenLesson?.(l.num)}
            />
          ))}
          <BreakthroughCard
            locked={completed < 5}
            onOpen={onOpenBreakthrough}
          />
        </div>
      </section>

      {/* SOCIAL PROOF strip */}
      <section
        className="relative mb-10 overflow-hidden rounded-[14px] border border-dashed border-fd-border-strong/40 bg-fd-paper p-6 sm:p-7"
      >
        <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto]">
          <div>
            <Eyebrow className="mb-2">PROOF · COHORT 03</Eyebrow>
            <h2 className="mb-2 font-geist text-[20px] font-medium tracking-[-0.015em]">
              Pamela ran Kickstart Tuesday. Wednesday her bridge was live.
            </h2>
            <p className="max-w-[560px] text-[13.5px] leading-[1.55] text-fd-text-muted">
              "Honestly the lessons aren't the win. The win is the Lab Work — by lesson 3 I had a
              draft of the bridge for my actual offer. It went out Friday and replies came in by
              Sunday." — Pamela J · PJPT clinic OS · cohort 03
            </p>
          </div>
          <div className="relative shrink-0">
            <Crowd variant="cheering" className="h-[100px] w-auto" />
            <MarginNote tilt={-3} tone="teal" size="sm" className="absolute -top-2 -left-2">
              cohort 03 shipped.
            </MarginNote>
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Cards ──────────────────────────────────────────────────────────────

function LessonCard({ lesson: l, onOpen }: { lesson: Lesson; onOpen?: () => void }) {
  const isLocked = l.state === 'locked';
  const isCurrent = l.state === 'current';
  const isDone = l.state === 'done';

  return (
    <article
      className={[
        'group relative flex flex-col overflow-hidden rounded-[14px] border-[1.5px] border-fd-text transition-all',
        isLocked ? 'opacity-60' : 'hover:-translate-y-1 hover:shadow-[0_5px_0_0_var(--fd-text)]',
      ].join(' ')}
      style={!isLocked ? { boxShadow: '3px 3px 0 0 var(--fd-text)' } : undefined}
    >
      {/* Video thumbnail / state band */}
      <div
        className={[
          'relative aspect-video w-full overflow-hidden',
          isCurrent ? 'bg-fd-accent' : isDone ? 'bg-fd-text' : 'bg-fd-surface-2',
        ].join(' ')}
      >
        {/* Big mono number */}
        <span
          className={[
            'absolute left-4 top-3 font-geist-mono text-[44px] font-semibold leading-none tracking-[-0.03em]',
            isCurrent ? 'text-fd-text' : isDone ? 'text-fd-accent' : 'text-fd-text-subtle/60',
          ].join(' ')}
        >
          {l.num}
        </span>
        {/* State pill */}
        <div className="absolute right-3 top-3">
          {isDone && <RaceBib n="" label="Watched" tone="ink" size="sm" shadow="none" />}
          {isCurrent && <RaceBib n="" label="Up next" tone="ink" size="sm" shadow="sm" />}
        </div>
        {/* Play / Lock icon centered */}
        <div className="absolute inset-0 grid place-items-center">
          <div
            className={[
              'grid h-14 w-14 place-items-center rounded-full border-[1.5px] border-fd-text transition-transform group-hover:scale-105',
              isCurrent ? 'bg-fd-text text-fd-accent' : isDone ? 'bg-fd-accent text-fd-text' : 'bg-fd-bg text-fd-text-muted',
            ].join(' ')}
          >
            {isLocked ? <Lock className="h-5 w-5" /> : isDone ? <Check className="h-5 w-5" /> : <Play className="h-5 w-5 fill-current" />}
          </div>
        </div>
        {/* Duration corner */}
        <div className="absolute bottom-3 right-3">
          <span className="inline-flex items-center gap-1 rounded-[3px] border border-fd-text bg-fd-bg px-1.5 py-0.5 font-geist-mono text-[10px] font-bold uppercase tracking-[0.04em] text-fd-text">
            <Clock className="h-2.5 w-2.5" />
            {l.duration}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5 bg-fd-paper">
        <h3 className="font-geist text-[17px] font-semibold leading-[1.2] tracking-[-0.015em] text-fd-text">
          {l.title}
        </h3>
        <p className="mt-1.5 flex-1 text-[13px] leading-[1.5] text-fd-text-muted">{l.preview}</p>

        <button
          disabled={isLocked}
          onClick={!isLocked ? onOpen : undefined}
          className={[
            'mt-4 inline-flex h-9 items-center justify-center gap-2 rounded-[4px] border font-geist text-[13px] font-medium transition-colors',
            isLocked
              ? 'cursor-not-allowed border-fd-border text-fd-text-subtle'
              : isCurrent
              ? 'border-fd-text bg-fd-text text-fd-accent hover:bg-[oklch(0.24_0.005_95)]'
              : isDone
              ? 'border-fd-border bg-fd-paper text-fd-text-muted hover:bg-fd-surface-1 hover:text-fd-text'
              : 'border-fd-text bg-fd-paper text-fd-text hover:bg-fd-surface-1',
          ].join(' ')}
        >
          {isLocked && <>Unlocks after Lesson {String(parseInt(l.num) - 1).padStart(2, '0')}</>}
          {isCurrent && <><Play className="h-3 w-3 fill-current" /> Continue</>}
          {isDone && <>Re-watch</>}
          {l.state === 'next' && <>Start lesson <ArrowRight className="h-3 w-3" /></>}
        </button>
      </div>
    </article>
  );
}

function BreakthroughCard({ locked, onOpen }: { locked: boolean; onOpen?: () => void }) {
  return (
    <article
      className={[
        'relative flex flex-col overflow-hidden rounded-[14px] border-[1.5px] border-fd-text',
        locked ? 'bg-fd-text text-fd-bg opacity-95' : 'bg-fd-accent text-fd-text',
      ].join(' ')}
      style={{ boxShadow: locked ? '4px 4px 0 0 var(--fd-accent)' : '4px 4px 0 0 var(--fd-text)' }}
    >
      {/* Top band */}
      <div className="relative aspect-video w-full">
        <div className="absolute left-4 top-3 inline-flex items-center gap-1.5">
          <Sparkles className={['h-4 w-4', locked ? 'text-fd-accent' : 'text-fd-text'].join(' ')} />
          <span
            className={[
              'font-geist-mono text-[10px] font-bold uppercase tracking-[0.10em]',
              locked ? 'text-fd-accent' : 'text-fd-text',
            ].join(' ')}
          >
            The Breakthrough
          </span>
        </div>
        <div className="absolute inset-0 grid place-items-center">
          <div
            className={[
              'grid h-16 w-16 place-items-center rounded-full border-[1.5px] border-fd-text',
              locked ? 'bg-fd-text text-fd-accent' : 'bg-fd-text text-fd-accent',
            ].join(' ')}
          >
            {locked ? <Lock className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
          </div>
        </div>
        <span className={['absolute bottom-3 right-3 font-geist-mono text-[10px] font-bold uppercase tracking-[0.06em]', locked ? 'text-fd-accent/80' : 'text-fd-text/80'].join(' ')}>
          {locked ? 'LOCKED · 5/5 LESSONS' : 'UNLOCKED · OPEN ↗'}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-geist text-[18px] font-semibold leading-[1.15] tracking-[-0.015em]">
          {locked ? 'Your first AI-native funnel.' : 'Open The Breakthrough →'}
        </h3>
        <p className={['mt-1.5 flex-1 text-[13px] leading-[1.5]', locked ? 'text-[oklch(0.78_0.005_95)]' : 'text-fd-text/85'].join(' ')}>
          Finish all five lessons + Lab Work to unlock. The Breakthrough is your concrete first
          move — a draft warm bridge for your real offer, plus the path to launch.
        </p>
        <button
          type="button"
          disabled={locked}
          onClick={!locked ? onOpen : undefined}
          className={[
            'mt-4 inline-flex h-9 items-center justify-center gap-2 rounded-[4px] border font-geist text-[13px] font-bold transition-colors',
            locked
              ? 'cursor-not-allowed border-fd-accent/40 text-fd-accent/60'
              : 'border-fd-text bg-fd-text text-fd-accent hover:bg-[oklch(0.24_0.005_95)]',
          ].join(' ')}
        >
          {locked ? 'Finish all 5 lessons to unlock' : <>Open The Breakthrough <ArrowRight className="h-3 w-3" /></>}
        </button>
      </div>
    </article>
  );
}
